import { useCallback, useState, useRef } from 'react';
import { generateValidId } from '../utils/messageUtils';
import { useMessageStatus } from './useMessageStatus';
import { authenticatedFetch } from '../../../utils/authenticatedFetch';

export const useMessageStreaming = ({ 
  setMessages, 
  setError, 
  setIsLoading, 
  sseStreamingActive, 
  currentUser,
  chatId,
  onChatCreated,
  projectId,
  clientId,
  toolCallManager
}) => {

  // State for deep research progress
  const [deepResearchProgress, setDeepResearchProgress] = useState(null);

  // Track active file-job polling timers so we can cancel on unmount
  const _fileJobPolls = useRef({});
  
  // Get unified loading helpers
  const { updateMessageStatus, getMessageStatus, clearMessageStatus, STATUS_TYPES } = useMessageStatus();

  /**
   * Attach a completed file job result to the matching message in state.
   * Called both from WebSocket message_complete and from the poll fallback.
   */
  const applyFileJobResult = useCallback((messageId, generatedFile, text) => {
    setMessages(prev => {
      const msgs = Array.isArray(prev) ? prev : [];
      return msgs.map(m => {
        if (m._id !== messageId) return m;
        return {
          ...m,
          generatedFile,
          isStreaming: false,
          isTemporary: false,
          isComplete: true,
          isGeneratingFile: false,
          _pendingFileJobId: undefined,
          ...(text != null ? { text } : {}),
        };
      });
    });
    clearMessageStatus(messageId);
  }, [setMessages, clearMessageStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Poll GET /api/file-jobs/:jobId every 4 s as a fallback for WebSocket delivery.
   * Stops automatically when the job is complete/error or after 10 min.
   */
  const _startFileJobPoll = useCallback((jobId, messageId) => {
    if (_fileJobPolls.current[jobId]) return; // already polling

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
    const MAX_POLLS = 150; // 150 × 4 s = 10 min
    let count = 0;

    const timerId = setInterval(async () => {
      count += 1;
      if (count > MAX_POLLS) {
        clearInterval(timerId);
        delete _fileJobPolls.current[jobId];
        return;
      }

      try {
        const resp = await authenticatedFetch(`${API_URL}/file-jobs/${jobId}`);
        if (!resp.ok) return;
        const job = await resp.json();

        if (job.status === 'complete' && job.generatedFile) {
          clearInterval(timerId);
          delete _fileJobPolls.current[jobId];
          applyFileJobResult(messageId, job.generatedFile, null);
          console.log('📄 FILE JOB POLL: job complete, applied result', jobId);
        } else if (job.status === 'error') {
          clearInterval(timerId);
          delete _fileJobPolls.current[jobId];
          console.error('📄 FILE JOB POLL: job failed', jobId, job.error);
        }
      } catch (_) {
        // Network error — keep retrying
      }
    }, 4000);

    _fileJobPolls.current[jobId] = timerId;
  }, [applyFileJobResult]);

  // Check for ongoing deep research after refresh
  const checkDeepResearchStatus = useCallback(async (messageId) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
      const token = localStorage.getItem('token');
      
      const response = await authenticatedFetch(`${API_URL}/chat/deep-research-status/${messageId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const status = await response.json();
        
        if (status.isDeepResearch && !status.isComplete && status.isTemporary) {
          // Restore deep research progress
          const progressData = {
            stage: status.stage || 'research',
            progress: status.progress || 0.5,
            message: `Resuming deep research analysis... (${status.stage})`
          };
          
          setDeepResearchProgress(progressData);
          
          // Also update the message in the array with progress data
          setMessages(prev => {
            const currentMessages = Array.isArray(prev) ? prev : [];
            return currentMessages.map(msg => {
              if (msg._id === messageId) {
                return {
                  ...msg,
                  isDeepResearch: true,
                  metadata: {
                    ...msg.metadata,
                    stage: progressData.stage,
                    progress: progressData.progress
                  },
                  text: `🧠 ${progressData.message}`
                };
              }
              return msg;
            });
          });
          
          return true;
        }
      }
    } catch (error) {
      console.warn('Failed to check deep research status:', error);
    }
    return false;
  }, []);

  // Function to restore deep research for incomplete messages
  const restoreDeepResearchForMessages = useCallback((messages) => {
    if (!Array.isArray(messages)) return;
    
    const incompleteDeepResearch = messages.find(msg => 
      msg.sender === 'bot' && 
      msg.isTemporary && 
      (msg.isDeepResearch || msg.text?.includes('🧠'))
    );
    
    if (incompleteDeepResearch) {
      checkDeepResearchStatus(incompleteDeepResearch._id);
    }
  }, [checkDeepResearchStatus]);

  const handleSend = useCallback(async (messageInput, provider, model, toolSelection = {}) => {
    if (!messageInput?.trim()) return;

    try {
      setError(null);
      
      // 🧪 Check for test user impersonation
      const activeTestUserId = localStorage.getItem('devActiveTestUserId');
      const useTestUser = activeTestUserId && localStorage.getItem('devTestOrgMode') === 'true';
      
      // Skip optimistic update if parent already did it (e.g. ChatInput for instant UI feedback)
      const skipOptimisticUpdate = toolSelection.skipOptimisticUpdate === true;
      if (!skipOptimisticUpdate) {
        // Add user message immediately - optimistic UI for low-latency feel
        const userMessage = {
          _id: generateValidId(),
          text: messageInput,
          sender: 'user',
          timestamp: new Date(),
          isTemporary: true,
          userId: useTestUser ? activeTestUserId : currentUser?.id
        };
        
        setMessages(prev => {
          const currentMessages = Array.isArray(prev) ? prev : [];
          return [...currentMessages, userMessage];
        });
        
        // Set loading state immediately - logo circle + loading indicator appear
        setIsLoading(true);
      }
      
      // Handle deep research tool
      if (toolSelection.useDeepResearch) {
        setDeepResearchProgress({
          stage: 'initialization',
          progress: 0.1,
          message: 'Initializing deep research analysis...'
        });
      }
      
      // Send to backend using STREAMING endpoints
      const token = localStorage.getItem('token');
      let endpoint, body;
      // Strip UI-only flags before sending to API
      const { skipOptimisticUpdate: _skip, ...apiToolSelection } = toolSelection;
      
      // Handle Deep Research tool - always use o4-mini-deep-research from OpenAI  
      if (toolSelection.useDeepResearch) {

        endpoint = chatId 
          ? `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages/stream`
          : `${process.env.REACT_APP_API_URL}/chat/new-with-message/stream`;
        body = {
          text: messageInput,
          provider: 'openai',
          model: 'o4-mini-deep-research',
          projectId: projectId,
          clientId: clientId,
          ...apiToolSelection
        };
      }
      // Handle CoreEngine selection (when provider and model are 'auto')
      else if (provider === 'auto' && model === 'auto') {

        endpoint = chatId
          ? `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages/stream`
          : `${process.env.REACT_APP_API_URL}/chat/new-with-message/stream`;
        body = { 
          text: messageInput, 
          useCoreEngine: true,
          projectId: projectId,
          clientId: clientId,
          ...apiToolSelection 
        };
        // To re-enable Claude thinking for CoreEngine routing, uncomment:
        // body.enableThinking = true;
        // body.thinkingBudget = 10000;

      } else {

        endpoint = chatId
          ? `${process.env.REACT_APP_API_URL}/chat/${chatId}/messages/stream`
          : `${process.env.REACT_APP_API_URL}/chat/new-with-message/stream`;
        body = { 
          text: messageInput, 
          provider: provider,
          model: model,
          projectId: projectId,
          clientId: clientId,
          ...apiToolSelection
        };
        // To auto-enable thinking for Claude models, uncomment:
        // if (provider === 'anthropic' || (model && (model.includes('claude') || model.includes('sonnet') || model.includes('opus')))) {
        //   body.enableThinking = true;
        //   body.thinkingBudget = 10000; // Default thinking budget
        // }
      }
      
      // Add test user headers and SSE headers
      const headers = {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        Authorization: `Bearer ${token}`,
      };
      
      if (useTestUser) {
        headers['x-test-user-id'] = activeTestUserId;
        const testOrgId = localStorage.getItem('devTestOrgId'); 
        if (testOrgId) {
          headers['x-test-org-id'] = testOrgId;
        }
      }

      // Stream request initiated
      

      const response = await authenticatedFetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Stream request failed:', response.status, errorText);
        throw new Error(`Request failed: ${response.status}`);
      }

      // Handle Server-Sent Events - Always use SSE for message streaming
      // Set streaming flag to block WebSocket interference
      sseStreamingActive.current = true;
      console.log('SSE: Starting SSE stream processing');
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let botMessage = null;
      let previousMessageId = null; // Track previous ID when updating from temp to real ID
      let thinkingJustCompleted = false; // Flag to prevent skipping content immediately after thinking
      
      // Helper to update botMessage in messages array, handling ID changes
      const updateBotMessage = (updatedMessage, oldId = null) => {
        const idToRemove = oldId || updatedMessage._id;
        setMessages(prev => {
          const currentMessages = Array.isArray(prev) ? prev : [];
          // Remove message with old ID and add updated message
          return [...currentMessages.filter(m => m._id !== idToRemove), updatedMessage];
        });
      };
    
      let currentEvent = null; // persist across reader.read() chunks so event: / data: pairs aren't split
                    while (true) {
        const { done, value } = await reader.read();
        if (done) {
          console.log('SSE: Stream ended - finalizing message');
            // Finalize bot message when stream ends
          if (botMessage) {
            // Clear any pending rAF flush
            if (botMessage._rafId) {
              cancelAnimationFrame(botMessage._rafId);
              botMessage._rafId = null;
            }
            
            // Clear loading-related flags to enable feedback buttons
            const finalMessage = {
              ...botMessage,
              isStreaming: false,
              isThinking: false,
              isTemporary: false,
              isSearching: false,
              isAnalyzing: false,
              isQueryingSuperbucket: false,
              isGeneratingImage: false,
              isComplete: true,
              text: botMessage._textBuffer || botMessage.text
            };
            
            console.log('SSE: Final message state:', {
              id: finalMessage._id,
              isStreaming: finalMessage.isStreaming,
              isThinking: finalMessage.isThinking,
              isTemporary: finalMessage.isTemporary,
              isTemp: finalMessage._id?.startsWith('temp-')
            });
            
            // Use helper to properly handle ID changes
            if (previousMessageId && previousMessageId !== finalMessage._id) {
              console.log('SSE: Removing old message with ID:', previousMessageId);
              updateBotMessage(finalMessage, previousMessageId);
            } else {
              setMessages(prev => {
                const currentMessages = Array.isArray(prev) ? prev : [];
                return currentMessages.map(msg => {
                  if (msg._id !== finalMessage._id) return msg;
                  // Preserve message_complete state (e.g. file gen from WebSocket)
                  if (msg.generatedFile && msg.isComplete && msg.text) {
                    return { ...msg, isStreaming: false, isTemporary: false, isComplete: true };
                  }
                  return finalMessage;
                });
              });
            }
            
            // Clear loading status to enable feedback buttons
            if (finalMessage._id) {
              console.log('SSE: Clearing loading status for message:', finalMessage._id);
              const currentStatus = getMessageStatus(finalMessage._id);
              // Don't clear thinking status - users should be able to view it after completion
              if (currentStatus.type !== STATUS_TYPES.CLAUDE_THINKING) {
                updateMessageStatus(finalMessage._id, {
                  type: STATUS_TYPES.COMPLETE,
                  data: {}
                });
              }
            }
          }
          
          setIsLoading(false);
          // Reset SSE streaming flag after delay to prevent WebSocket race
          setTimeout(() => {
            sseStreamingActive.current = false;
          }, 100);
          break;
        }
        
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
          for (const line of lines) {
            if (line.startsWith('event: ')) {
              currentEvent = line.slice(7).trim();
                              // Log only important events (reduced for cleaner output)
                if (['thinking_start', 'thinking_delta', 'thinking_complete', 'done'].includes(currentEvent)) {
                  console.log('EVENT:', currentEvent);
                }
              continue;
            } else if (line.startsWith('data: ')) {
              const content = line.slice(6);
              if (content.trim() === '') continue;
              
              try {
                const data = JSON.parse(content);
                
                if (data.messageId || currentEvent === 'done' || currentEvent === 'message_created') {
                  console.log('SSE EVENT:', currentEvent, {
                    messageId: data.messageId,
                    botMessageId: botMessage?._id,
                    dataKeys: Object.keys(data)
                  });
                }
                
                // Route thinking_delta content to the proper handler
                if (currentEvent === 'thinking_delta' && data.content) {
                  
                  // Process this as a thinking_delta event, not regular content
                  const messageId = data.messageId || botMessage?._id;
                  const thinkingDelta = data.content || '';
                  
                  if (!botMessage) {
                    continue;
                  }
                  
                  // Update the bot message with thinking content
                  const oldId = botMessage._id;
                  if (data.messageId && botMessage._id !== data.messageId) {
                    console.log('Updating message ID from', botMessage._id, 'to', data.messageId);
                    previousMessageId = botMessage._id;
                    botMessage._id = data.messageId;
                  }
                  
                  const newThinkingContent = (botMessage.thinkingContent || '') + thinkingDelta;
                  
                  botMessage = {
                    ...botMessage,
                    thinkingContent: newThinkingContent
                  };
                  
                  updateBotMessage(botMessage, previousMessageId || oldId);
                  
                  // Update unified loading system with new thinking content
                  const currentDuration = botMessage.thinkingStartTime ? (Date.now() - botMessage.thinkingStartTime) / 1000 : 0;
                  
                  updateMessageStatus(botMessage._id, {
                    type: STATUS_TYPES.CLAUDE_THINKING,
                    data: {
                      isThinking: true,
                      thinkingContent: newThinkingContent,
                      duration: currentDuration
                    }
                  });
                  
                  continue;
                }
                
                // Handle message_start - use real message ID from the start (Anthropic sends this first)
                if (currentEvent === 'message_start' && data.messageId) {
                  if (!botMessage) {
                    botMessage = {
                      _id: data.messageId,
                      text: '',
                      sender: 'bot',
                      timestamp: new Date(),
                      isTemporary: false,
                      isStreaming: true,
                      isThinking: false,
                      _textBuffer: ''
                    };
                    setMessages(prev => {
                      const currentMessages = Array.isArray(prev) ? prev : [];
                      return [...currentMessages, botMessage];
                    });
                  } else if (botMessage._id !== data.messageId && botMessage._id?.startsWith?.('temp-')) {
                    previousMessageId = botMessage._id;
                    botMessage._id = data.messageId;
                    setMessages(prev => {
                      const list = Array.isArray(prev) ? prev : [];
                      const existing = list.find(m => m._id === previousMessageId);
                      if (!existing) return prev;
                      return list.map(m => m._id === previousMessageId ? { ...m, _id: data.messageId } : m);
                    });
                  }
                  continue;
                }
                
                // Handle regular content chunks (non-event based) with buffering
                if (data.content && !data.type) {
                  // Skip regular content when in thinking mode (but NOT if thinking just completed)
                  if (botMessage && botMessage.isClaudeThinking && !thinkingJustCompleted) {
                    continue;
                  }
                  
                  // Clear the flag after first content chunk following thinking completion
                  if (thinkingJustCompleted) {
                    thinkingJustCompleted = false;
                  }
                  
                  // Buffer content chunks for smooth display
                  if (!botMessage) {
                    const tempId = `temp-streaming-${Date.now()}`;
                    console.log('🤖 Creating initial botMessage with temp ID:', tempId);
                    botMessage = {
                      _id: tempId,
                      text: data.content, // Set text immediately with first chunk
                      sender: 'bot',
                      timestamp: new Date(),
                      isTemporary: false,
                      isStreaming: true,
                      isThinking: false,
                      _textBuffer: data.content // Internal buffer for smooth streaming
                    };
                    

                    
                    setMessages(prev => {
                      const currentMessages = Array.isArray(prev) ? prev : [];
                      
                      // Don't filter out temporary messages when adding initial bot message
                      // The previous code: [...currentMessages.filter(m => !m.isTemporary), botMessage]
                      // was removing the temporary user message that was just sent, causing the bot
                      // message to be placed at the wrong array index and associated with the wrong
                      // conversation pair. This led to old user messages appearing during streaming.
                      const newMessages = [...currentMessages, botMessage];
                      
                      return newMessages;
                    });
                  } else {
                    // Buffer the content instead of immediately appending
                    const shouldReplace = !botMessage.text || botMessage.text === '' || data.replace === true;
                    // When replacing empty text, preserve existing buffer content
                    const newBufferContent = shouldReplace 
                      ? (botMessage._textBuffer || '') + data.content 
                      : (botMessage._textBuffer || botMessage.text) + data.content;
                    
                    // Clear loading indicators when content starts streaming
                    if (shouldReplace) {
                      // Clear superbucket loading status when content starts
                      if (botMessage.isQueryingSuperbucket) {
                        console.log('SUPERBUCKET: Clearing loading status - content streaming started');
                        updateMessageStatus(botMessage._id, {
                          type: STATUS_TYPES.COMPLETE,
                          data: {}
                        });
                      }
                      
                      // Clear image generation loading status when content starts
                      if (botMessage.isGeneratingImage) {
                        console.log('IMAGE: Clearing loading status - content streaming started');
                        updateMessageStatus(botMessage._id, {
                          type: STATUS_TYPES.COMPLETE,
                          data: {}
                        });
                      }
                    }
                    
                    botMessage = {
                      ...botMessage,
                      _textBuffer: newBufferContent,
                      isStreaming: true,
                      isQueryingSuperbucket: false, // Clear superbucket flag
                      isGeneratingImage: false // Clear image generation flag
                    };
                    
                                        // Update message state to show content starting
                    if (shouldReplace) {
                      // Update botMessage.text immediately to stay in sync
                      botMessage.text = newBufferContent;
                      
                      setMessages(prev => {
                        const currentMessages = Array.isArray(prev) ? prev : [];
                        return currentMessages.map(msg => 
                          msg._id === botMessage._id 
                            ? { 
                                ...msg, 
                                text: newBufferContent, // Show the buffer content immediately
                                isQueryingSuperbucket: false,
                                isGeneratingImage: false,
                                isStreaming: true
                              }
                            : msg
                        );
                      });
                      
                      // Skip the timeout for the first chunk since we already updated immediately
                      continue;
                    }
                    
                    
                    
                    // Batch state updates to the next animation frame so chunks
                    // arriving in the same frame are coalesced into one render.
                    if (!botMessage._rafId) {
                      botMessage._rafId = requestAnimationFrame(() => {
                        const newText = botMessage._textBuffer;
                        botMessage.text = newText;
                        setMessages(prev => {
                          const currentMessages = Array.isArray(prev) ? prev : [];
                          return currentMessages.map(msg =>
                            msg._id === botMessage._id
                              ? { ...msg, text: newText }
                              : msg
                          );
                        });
                        botMessage._rafId = null;
                      });
                    }
                  }
                  continue;
                }
                
                // Process events only for important ones (reduced for cleaner output)
                if (['thinking_start', 'thinking_delta', 'thinking_complete', 'done'].includes(currentEvent)) {
                  console.log('Processing:', currentEvent);
                }
                
                // Handle file generation events
              if (currentEvent === 'file_generation_start') {
                // File gen now uses mcp_tool_start for tool indicator (terminal icon) - don't set GENERATING_FILE
                // to avoid showing the progress bar. The tool indicator from mcp_tool_start is preferred.
                console.log('📄 FILE: File generation started (tool indicator used via mcp_tool_start)');
                if (botMessage && data.messageId && botMessage._id !== data.messageId) {
                  previousMessageId = botMessage._id;
                  botMessage._id = data.messageId;
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    const existing = currentMessages.find(m => m._id === previousMessageId);
                    if (!existing) return prev;
                    return [...currentMessages.filter(m => m._id !== previousMessageId), { ...existing, _id: data.messageId }];
                  });
                }
                continue;
              }
              
              // Stream code preview chunks for file generation (shows progress under the tool pill)
              if (currentEvent === 'file_gen_code_chunk') {
                const targetId = data.messageId || botMessage?._id;
                if (targetId) {
                  setMessages(prev => {
                    const msgs = Array.isArray(prev) ? prev : [];
                    return msgs.map(m => {
                      if (m._id !== targetId) return m;
                      const activeFileTool = (m._activeFileTool || '').toLowerCase();
                      const toolNames = (m.toolCallPositions || [])
                        .map((pos) => (pos?.functionName || '').toLowerCase())
                        .filter(Boolean);
                      const isPptxTool =
                        activeFileTool.includes('powerpoint') ||
                        activeFileTool.includes('pptx') ||
                        toolNames.some((name) => name.includes('powerpoint') || name.includes('pptx'));
                      if (!isPptxTool) return m;
                      const existing = m._codePreview || '';
                      return { ...m, _codePreview: existing + (data.chunk || '') };
                    });
                  });
                  if (botMessage && (botMessage._id === targetId)) {
                    const activeFileTool = (botMessage._activeFileTool || '').toLowerCase();
                    const isPptxTool = activeFileTool.includes('powerpoint') || activeFileTool.includes('pptx');
                    if (!isPptxTool) {
                      continue;
                    }
                    botMessage._codePreview = (botMessage._codePreview || '') + (data.chunk || '');
                  }
                }
                continue;
              }

              // Handle file generation progress updates - mcp_tool_complete handles completion now
              if (currentEvent === 'file_progress') {
                if (data.progress === 100) {
                  console.log('📄 FILE: File generation complete (progress 100)');
                  if (botMessage) {
                    botMessage = { ...botMessage, isGeneratingFile: false };
                    updateBotMessage(botMessage);
                  }
                  const targetMessageId = data.messageId || botMessage?._id;
                  if (targetMessageId) clearMessageStatus(targetMessageId);
                }
                continue;
              }
              
              // Handle background file generation job start
              if (currentEvent === 'file_job_started') {
                console.log('[FILE-JOB SSE] file_job_started received', {
                  jobId: data.jobId,
                  format: data.format,
                  backendMessageId: data.messageId,
                  frontendBotMessageId: botMessage?._id,
                  idsMatch: data.messageId === botMessage?._id,
                });
                const targetId = data.messageId || botMessage?._id;
                if (targetId) {
                  // Store jobId on the message so the WebSocket handler can do a targeted poll
                  setMessages(prev => {
                    const msgs = Array.isArray(prev) ? prev : [];
                    return msgs.map(m =>
                      m._id === targetId ? { ...m, _pendingFileJobId: data.jobId } : m
                    );
                  });
                  if (botMessage && botMessage._id === targetId) {
                    botMessage = { ...botMessage, _pendingFileJobId: data.jobId };
                  }
                  // Poll the job endpoint as a fallback in case WebSocket is down
                  _startFileJobPoll(data.jobId, targetId);
                }
                continue;
              }

              // Handle image generation events based on event name
              if (currentEvent === 'image_generation_start') {
                console.log('IMAGE: Image generation started, setting loading state');
                console.log('IMAGE: Backend messageId:', data.messageId);
                console.log('IMAGE: Frontend botMessage._id:', botMessage?._id);
                console.log('IMAGE: MessageIds match:', data.messageId === botMessage?._id);
                
                // Create botMessage if it doesn't exist (for image generation)
                if (!botMessage && data.messageId) {
                  console.log('🤖 BOT: Creating bot message for image generation with ID:', data.messageId);
                  botMessage = {
                    _id: data.messageId,
                    text: '[[LOADING_INDICATOR]]',
                    sender: 'bot',
                    timestamp: new Date(),
                    isTemporary: false,
                    isStreaming: true,
                    isThinking: false,
                    isGeneratingImage: true,
                    optimalImageSize: data.optimalSize || 'auto'
                  };
                  
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    return [...currentMessages.filter(m => !m.isTemporary), botMessage];
                  });
                }
                
                // Update the unified loading status - use correct message ID
                const targetMessageId = data.messageId || botMessage?._id;
                if (targetMessageId) {
                  updateMessageStatus(targetMessageId, {
                    type: STATUS_TYPES.GENERATING_IMAGE,
                    data: {
                      optimalSize: data.optimalSize || 'auto'
                    }
                  });
                } else {
                  console.warn('IMAGE: No target message ID available for status update');
                }
                
                // Also set the flag on the bot message for fallback compatibility
                if (botMessage) {
                  // Update botMessage ID to match backend if different
                  const oldId = botMessage._id;
                  if (data.messageId && botMessage._id !== data.messageId) {
                    console.log('IMAGE: Updating botMessage ID to match backend:', data.messageId);
                    previousMessageId = botMessage._id;
                    botMessage._id = data.messageId;
                  }
                  
                  botMessage = {
                    ...botMessage,
                    isGeneratingImage: true,
                    optimalImageSize: data.optimalSize || 'auto'
                  };
                  
                  updateBotMessage(botMessage, previousMessageId || oldId);
                }
                continue;
              }
              
              // Handle image generation progress updates
              if (currentEvent === 'image_progress') {
                // FALLBACK: If this is the first progress event and we're generating, treat it as start
                if (data.isGeneratingImage === true && data.messageId) {
                  console.log('IMAGE: First progress event detected, treating as generation start');
                  
                  // Create botMessage if it doesn't exist (fallback for missed start event)
                  if (!botMessage) {
                    console.log('🤖 BOT: Creating bot message for image generation (progress fallback) with ID:', data.messageId);
                    botMessage = {
                      _id: data.messageId,
                      text: '[[LOADING_INDICATOR]]',
                      sender: 'bot',
                      timestamp: new Date(),
                      isTemporary: false,
                      isStreaming: true,
                      isThinking: false,
                      isGeneratingImage: true,
                      optimalImageSize: data.optimalSize || 'auto'
                    };
                    
                    setMessages(prev => {
                      const currentMessages = Array.isArray(prev) ? prev : [];
                      return [...currentMessages.filter(m => !m.isTemporary), botMessage];
                    });
                  }
                  
                                     // Always set the generating image status to ensure it's active
                   console.log('IMAGE: Setting GENERATING_IMAGE status from progress event');
                   updateMessageStatus(data.messageId, {
                     type: STATUS_TYPES.GENERATING_IMAGE,
                     data: {
                       optimalSize: data.optimalSize || 'auto'
                     }
                   });
                }
                
                if (data.isGeneratingImage === false) {
                  console.log('IMAGE: Image generation progress completed, but keeping loading state until image_generated');
                  
                  // DON'T clear the loading state yet - wait for image_generated event
                  // Keep the GENERATING_IMAGE status active until we get the actual image
                  
                  // Clear the flag on the bot message
                  if (botMessage) {
                    botMessage = {
                      ...botMessage,
                      isGeneratingImage: false
                    };
                    
                    setMessages(prev => {
                      const currentMessages = Array.isArray(prev) ? prev : [];
                      return [...currentMessages.filter(m => m._id !== botMessage._id), botMessage];
                    });
                  }
                }
                continue;
              }
              
              // Handle image generation completion with image data
              if (currentEvent === 'image_generated') {
                console.log('IMAGE: Image generation completed with image data, clearing loading state');
                
                // Clear the generating image status
                updateMessageStatus(data.messageId, {
                  type: STATUS_TYPES.COMPLETE,
                  data: {}
                });
                
                // Update bot message with generated image if we have a bot message
                if (botMessage) {
                  const updatedMessage = {
                    ...botMessage,
                    isGeneratingImage: false,
                    isStreaming: false,
                    isTemporary: false
                  };
                  
                  // Add image data if available
                  if (data.imageData) {
                    updatedMessage.generatedImage = data.imageData;
                    // Clear any "Creating image" text and let the MessageDisplay component handle showing the image
                    if (updatedMessage.text === 'Creating image') {
                      updatedMessage.text = '';
                    }
                  }
                  
                  botMessage = updatedMessage;
                  
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    return [...currentMessages.filter(m => m._id !== botMessage._id), botMessage];
                  });
                }
                continue;
              }
              
              // Handle connection event with new chat ID
              if (currentEvent === 'connect') {
                console.log('🔗 SSE connection established');
                
                // Handle new chat ID for first messages
                if (data.chatId && !chatId && onChatCreated) {
                  console.log('New chat ID received via connect event:', data.chatId);
                  onChatCreated(data.chatId);
                }
                continue;
              }
              
              // Handle MCP tool start - simple like mobile: find message by ID, add toolCallPositions
              if (currentEvent === 'mcp_tool_start') {
                const targetId = data.messageId || botMessage?._id;
                if (!targetId) { continue; }
                const position = data.textPosition ?? 0;
                const newPosition = { position, toolId: data.toolId, functionName: data.functionName, type: data.type || 'mcp_tool', isComplete: false, status: data.status };
                const isFileGen = data.type === 'file_generation' || (data.functionName && data.functionName.startsWith('generate_'));
                const activeFileTool = isFileGen ? data.functionName : undefined;

                if (toolCallManager) {
                  toolCallManager.handleToolCallStart(targetId, { toolName: data.functionName, toolId: data.toolId, status: data.status || 'Executing...', connector: 'hubspot_direct', messageId: targetId, position });
                }

                setMessages(prev => {
                  const list = Array.isArray(prev) ? prev : [];
                  let existing = list.find(m => m._id === targetId);
                  // Like mobile: if backend sent real ID but we have temp-ID message, migrate it
                  if (!existing && data.messageId && botMessage) {
                    const tempMatch = list.find(m => m._id === botMessage._id);
                    if (tempMatch) {
                      previousMessageId = botMessage._id;
                      botMessage._id = data.messageId;
                      const newPositions = [...(tempMatch.toolCallPositions || []), newPosition];
                      return list.map(m => m._id === previousMessageId
                        ? { ...m, _id: data.messageId, toolCallPositions: newPositions, toolCallsActive: true, hasToolCalls: true, isSearching: isFileGen ? false : m.isSearching, ...(activeFileTool ? { _activeFileTool: activeFileTool } : {}) }
                        : m);
                    }
                  }
                  if (existing) {
                    const newPositions = [...(existing.toolCallPositions || []), newPosition];
                    return list.map(m => m._id === targetId
                      ? { ...m, toolCallPositions: newPositions, toolCallsActive: true, hasToolCalls: true, isSearching: isFileGen ? false : m.isSearching, ...(activeFileTool ? { _activeFileTool: activeFileTool } : {}) }
                      : m);
                  }
                  // No message yet - create placeholder (like mobile)
                  const placeholder = { _id: targetId, text: '', sender: 'bot', timestamp: new Date(), isTemporary: false, isStreaming: true, toolCallPositions: [newPosition], toolCallsActive: true, hasToolCalls: true, ...(activeFileTool ? { _activeFileTool: activeFileTool } : {}) };
                  return [...list, placeholder];
                });
                if (botMessage && (botMessage._id === targetId || !botMessage.toolCallPositions)) {
                  botMessage = { ...botMessage, _id: targetId, toolCallPositions: [...(botMessage.toolCallPositions || []), newPosition], toolCallsActive: true, hasToolCalls: true, ...(activeFileTool ? { _activeFileTool: activeFileTool } : {}) };
                } else if (!botMessage && targetId) {
                  botMessage = { _id: targetId, text: '', sender: 'bot', timestamp: new Date(), isTemporary: false, isStreaming: true, toolCallPositions: [newPosition], toolCallsActive: true, hasToolCalls: true, ...(activeFileTool ? { _activeFileTool: activeFileTool } : {}) };
                }
                continue;
              }
              
              // Handle MCP tool completion events (align with mobile: update toolCallPositions with status, recordCount)
              if (currentEvent === 'mcp_tool_complete') {
                console.log('MCP TOOL COMPLETE:', data.functionName, 'Success:', data.success);
                
                const messageId = data.messageId || botMessage?._id;

                // Attach generatedFile immediately when the file tool completes —
                // don't wait for the WebSocket message_complete which can race with sseStreamingActive
                if (data.generatedFile && messageId) {
                  if (botMessage && botMessage._id === messageId) {
                    botMessage = { ...botMessage, generatedFile: data.generatedFile };
                  }
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    return currentMessages.map(msg =>
                      msg._id === messageId ? { ...msg, generatedFile: data.generatedFile } : msg
                    );
                  });
                }
                
                // Update tool call result in manager (pass status and recordCount for detailed display)
                if (toolCallManager && messageId) {
                  toolCallManager.handleToolCallResult(messageId, {
                    toolName: data.functionName,
                    toolId: data.toolId,
                    success: data.success,
                    result: data.success ? data.result : null,
                    error: data.success ? null : data.error || 'Tool execution failed',
                    connector: 'hubspot_direct',
                    status: data.status,
                    recordCount: data.recordCount
                  });
                }
                
                if (messageId) {
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    const existing = currentMessages.find(m => m._id === messageId);
                    if (!existing) return prev;
                    const positions = existing.toolCallPositions || [];
                    const matchIdx = data.toolId
                      ? positions.findIndex((p) => p.toolId === data.toolId)
                      : positions.findIndex((p) => p.functionName === data.functionName && !p.isComplete);
                    const updatedPositions =
                      matchIdx >= 0
                        ? positions.map((p, i) =>
                            i === matchIdx ? { ...p, isComplete: true, status: data.status, recordCount: data.recordCount } : p
                          )
                        : positions;
                    const isHubSpotTool = data.functionName?.startsWith('hubspot_') || data.functionName?.includes('hubspot');
                    const hubspotResult = isHubSpotTool ? {
                      functionName: data.functionName,
                      success: data.success,
                      summary: data.success ? data.status || `Successfully executed ${data.functionName}` : `Error in ${data.functionName}`,
                      recordCount: data.recordCount || 0,
                      type: 'mcp_tool_result'
                    } : null;
                    const existingHubSpotResults = existing.hubspotFunctionResults || [];
                    const newHubSpotResults = hubspotResult ? [...existingHubSpotResults, hubspotResult] : existingHubSpotResults;
                    const allComplete = updatedPositions.every((p) => p.isComplete);
                    const updated = {
                      ...existing,
                      isSearching: false,
                      hasHubSpotFunction: newHubSpotResults.length > 0,
                      hubspotFunctionResults: newHubSpotResults,
                      toolCallsActive: !allComplete,
                      toolCallPositions: updatedPositions.length ? updatedPositions : existing.toolCallPositions,
                      _codePreview: allComplete ? undefined : existing._codePreview,
                    };
                    if (botMessage && botMessage._id === messageId) botMessage = updated;
                    return currentMessages.map(m => m._id === messageId ? updated : m);
                  });
                }
                continue;
              }
              
              // Handle stream completion events
              if (currentEvent === 'done') {
                console.log('STREAM: Stream completed, clearing loading states (preserving thinking)');

                // Attach generatedFile from done event — fallback in case mcp_tool_complete
                // fired before the message existed, or for non-Anthropic providers
                if (data.generatedFile && data.messageId) {
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    return currentMessages.map(msg =>
                      msg._id === data.messageId && !msg.generatedFile
                        ? { ...msg, generatedFile: data.generatedFile }
                        : msg
                    );
                  });
                  if (botMessage && botMessage._id === data.messageId && !botMessage.generatedFile) {
                    botMessage = { ...botMessage, generatedFile: data.generatedFile };
                  }
                }
                
                // Update botMessage ID if backend sent real ID
                const oldId = botMessage?._id;
                if (data.messageId && botMessage && botMessage._id !== data.messageId) {
                  console.log('DONE: Updating message ID from temp to real:', {
                    from: botMessage._id,
                    to: data.messageId
                  });
                  previousMessageId = botMessage._id;
                  botMessage._id = data.messageId;
                }
                
                // Clear loading states but preserve thinking status
                if (data.messageId) {
                  const currentStatus = getMessageStatus(data.messageId);
                  
                  // Don't clear thinking status - users should be able to view it after completion
                  if (currentStatus.type !== STATUS_TYPES.CLAUDE_THINKING) {
                    updateMessageStatus(data.messageId, {
                      type: STATUS_TYPES.COMPLETE,
                      data: {}
                    });
                  } else {
            
                  }
                } else if (botMessage && botMessage.thinkingContent) {
                  // If we have thinking content but no explicit messageId, preserve thinking status

                }
                
                // Finalize the bot message - DON'T interrupt the smooth streaming
                if (botMessage) {
                  // Cancel any pending rAF flush and apply final buffer
                  if (botMessage._rafId) {
                    cancelAnimationFrame(botMessage._rafId);
                    botMessage._rafId = null;
                  }
                  if (botMessage._textBuffer) {
                    botMessage.text = botMessage._textBuffer;
                  }
                  
                  console.log('🎯 DONE event - finalizing message with ID:', botMessage._id, 'isTemporary:', false);
                  
                  // Update botMessage properties
                  botMessage.isStreaming = false;
                  botMessage.isTemporary = false;
                  botMessage.isComplete = true;
                  
                  // Update in-place using helper to handle ID changes
                  if (previousMessageId && previousMessageId !== botMessage._id) {
                    // ID was changed, use helper to remove old message
                    const finalMessage = { ...botMessage, text: botMessage.text, isStreaming: false, isTemporary: false, isComplete: true };
                    updateBotMessage(finalMessage, previousMessageId);
                    botMessage = finalMessage;
                  } else {
                    // ID didn't change, just update in place
                    setMessages(prev => {
                      const currentMessages = Array.isArray(prev) ? prev : [];
                      return currentMessages.map(msg => {
                        if (msg._id !== botMessage._id) return msg;
                        // Preserve message_complete state (e.g. file gen): don't overwrite text/generatedFile
                        const hasCompletedFromWebSocket = msg.generatedFile && msg.isComplete;
                        const textToUse = hasCompletedFromWebSocket && msg.text
                          ? msg.text
                          : (botMessage._textBuffer || botMessage.text);
                        return {
                          ...msg,
                          text: textToUse,
                          isStreaming: false,
                          isTemporary: false,
                          isComplete: true
                        };
                      });
                    });
                  }
                }
                
                setIsLoading(false);
                
                // Delay reset to prevent WebSocket race condition
                setTimeout(() => {
                  sseStreamingActive.current = false;
                }, 100);
                continue;
              }
              
              // Handle search events - FIXED: Handle web_search events from Anthropic streaming
              if (currentEvent === 'web_search_start' || currentEvent === 'search_start' || (data.isSearching && data.searchStatus)) {
                console.log('SEARCH START: messageId:', data.messageId, 'status:', data.status);
                
                const messageId = data.messageId || botMessage?._id;
                
                // Create botMessage if it doesn't exist (search started before content)
                if (!botMessage && messageId) {
                  console.log('🤖 Creating bot message for search:', messageId);
                  botMessage = {
                    _id: messageId,
                    text: '[[LOADING_INDICATOR]]',
                    sender: 'bot',
                    timestamp: new Date(),
                    isTemporary: false,
                    isStreaming: true,
                    isThinking: false,
                    isSearching: true,
                    searchStatus: data.status || 'Searching the web...',
                    searchSources: []
                  };
                  
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    return [...currentMessages.filter(m => !m.isTemporary && m._id !== messageId), botMessage];
                  });
                } else if (botMessage) {
                  // Set search properties on the existing bot message for SearchIndicator detection
                  botMessage = {
                    ...botMessage,
                    isSearching: true,
                    searchStatus: data.status || 'Searching the web...',
                    searchSources: []
                  };
                  
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    return [...currentMessages.filter(m => m._id !== botMessage._id), botMessage];
                  });
                }
                
                // Update unified loading system
                if (messageId) {
                  const isHubSpot = data.searchStatus?.includes('HubSpot') || data.hubspotFunction;
                  updateMessageStatus(messageId, {
                    type: STATUS_TYPES.SEARCHING,
                    data: {
                      message: data.status || data.searchStatus || (isHubSpot ? 'Searching HubSpot...' : 'Searching the web...'),
                      sources: data.searchSources || [],
                      isHubSpot: isHubSpot
                    }
                  });
                }
                continue;
              }
              
              // Handle web search results - NEW: Handle search sources
              if (currentEvent === 'web_search_results' && data.sources && Array.isArray(data.sources)) {
                console.log('SEARCH RESULTS: messageId:', data.messageId, 'sources:', data.sources.length);
                
                const messageId = data.messageId || botMessage?._id;
                
                // Update bot message with search sources for SearchIndicator
                if (botMessage && botMessage._id === messageId) {
                  botMessage = {
                    ...botMessage,
                    isSearching: true,
                    searchStatus: data.status || 'Found search results',
                    searchSources: data.sources
                  };
                  
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    return [...currentMessages.filter(m => m._id !== botMessage._id), botMessage];
                  });
                } else if (messageId) {
                  // Update message by ID if botMessage doesn't match
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    return currentMessages.map(msg => {
                      if (msg._id === messageId) {
                        return {
                          ...msg,
                          isSearching: true,
                          searchStatus: data.status || 'Found search results',
                          searchSources: data.sources
                        };
                      }
                      return msg;
                    });
                  });
                }
                continue;
              }
              
              // Handle search completion - FIXED: Handle web_search_complete
              if (currentEvent === 'web_search_complete' || currentEvent === 'search_complete' || (data.hasSearched && !data.isSearching)) {
                console.log('SEARCH COMPLETE: messageId:', data.messageId, 'sources:', data.sources?.length);
                
                const messageId = data.messageId || botMessage?._id;
                
                // Set final search properties on the bot message
                if (botMessage && botMessage._id === messageId) {
                  botMessage = {
                    ...botMessage,
                    isSearching: false,
                    hasWebSearch: true,
                    searchStatus: data.status || 'Search complete',
                    searchSources: data.sources || botMessage.searchSources || []
                  };
                  
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    return [...currentMessages.filter(m => m._id !== botMessage._id), botMessage];
                  });
                } else if (messageId) {
                  // Update message by ID if botMessage doesn't match
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    return currentMessages.map(msg => {
                      if (msg._id === messageId) {
                        return {
                          ...msg,
                          isSearching: false,
                          hasWebSearch: true,
                          searchStatus: data.status || 'Search complete',
                          searchSources: data.sources || msg.searchSources || []
                        };
                      }
                      return msg;
                    });
                  });
                }
                
                // Update unified loading system to clear search status
                if (messageId) {
                  updateMessageStatus(messageId, {
                    type: STATUS_TYPES.COMPLETE,
                    data: {}
                  });
                }
                continue;
              }

              // Handle Claude thinking events
              if (currentEvent === 'thinking_start') {
          
                
                const messageId = data.messageId;
                
                // Create botMessage if it doesn't exist yet
                if (!botMessage && messageId) {
                  botMessage = {
                    _id: messageId,
                    text: '[[LOADING_INDICATOR]]',
                    sender: 'bot',
                    timestamp: new Date(),
                    isTemporary: false,
                    isStreaming: true,
                    isClaudeThinking: true,
                    thinkingContent: '',
                    thinkingStartTime: Date.now()
                  };
                  
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    return [...currentMessages, botMessage];
                  });
                } else if (botMessage) {
                  // Update existing botMessage with thinking state
                  // Update botMessage ID to match backend if different
                  const oldId = botMessage._id;
                  if (data.messageId && botMessage._id !== data.messageId) {
                    console.log('THINKING_START: Updating message ID from', botMessage._id, 'to', data.messageId);
                    previousMessageId = botMessage._id;
                    botMessage._id = data.messageId;
                  }
                  
                  botMessage = {
                    ...botMessage,
                    isClaudeThinking: true,
                    thinkingContent: '',
                    thinkingStartTime: Date.now()
                  };
                  
                  updateBotMessage(botMessage, previousMessageId || oldId);
                }
                
                // Update unified loading system for thinking (only if botMessage exists)
                if (botMessage) {
                  updateMessageStatus(botMessage._id, {
                    type: STATUS_TYPES.CLAUDE_THINKING,
                    data: {
                      isThinking: true,
                      thinkingContent: '',
                      duration: 0
                    }
                  });
                }
                continue;
              }



              if (currentEvent === 'thinking_complete') {
                
                const messageId = data.messageId || botMessage?._id;
                const duration = data.duration || 0;
                const fullContent = data.fullContent || '';
                

                
                // Update the bot message with final thinking state
                if (botMessage) {
                  // Update botMessage ID to match backend if different
                  const oldId = botMessage._id;
                  if (data.messageId && botMessage._id !== data.messageId) {
                    console.log('THINKING_COMPLETE: Updating message ID from', botMessage._id, 'to', data.messageId);
                    previousMessageId = botMessage._id;
                    botMessage._id = data.messageId;
                  }
                  
                  // Preserve existing text content during transition
                  const existingText = botMessage.text !== '[[LOADING_INDICATOR]]' ? botMessage.text : '[[LOADING_INDICATOR]]';
                  
                  // Create new botMessage with thinking completed
                  const updatedBotMessage = {
                    ...botMessage,
                    isClaudeThinking: false,
                    thinkingContent: fullContent,
                    thinkingDuration: duration,
                    text: existingText // Preserve existing text during transition
                  };
                  
                  // Update botMessage reference for subsequent content processing
                  botMessage = updatedBotMessage;
                  
                  // Set flag to prevent content skipping after thinking completes
                  thinkingJustCompleted = true;
                  
                  // Update state so content streaming sees the updated botMessage
                  updateBotMessage(botMessage, previousMessageId || oldId);
                
                  // Update unified loading system with completed thinking
                  updateMessageStatus(botMessage._id, {
                    type: STATUS_TYPES.CLAUDE_THINKING,
                    data: {
                      isThinking: false,
                      thinkingContent: fullContent,
                      duration: duration
                    }
                  });
                }
                continue;
              }

              // Handle superbucket events
              if (currentEvent === 'superbucket_start') {
                console.log('SUPERBUCKET: Query started, setting loading state');
                console.log('SUPERBUCKET: Backend messageId:', data.messageId);
                console.log('SUPERBUCKET: Frontend botMessage._id:', botMessage?._id);
                console.log('SUPERBUCKET: MessageIds match:', data.messageId === botMessage?._id);
                
                // Create botMessage if it doesn't exist (for superbucket queries)
                if (!botMessage && data.messageId) {
                  console.log('🤖 BOT: Creating bot message for superbucket query with ID:', data.messageId);
                  botMessage = {
                    _id: data.messageId,
                    text: '[[LOADING_INDICATOR]]',
                    sender: 'bot',
                    timestamp: new Date(),
                    isTemporary: false,
                    isStreaming: true,
                    isThinking: false,
                    isQueryingSuperbucket: true
                  };
                  
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    return [...currentMessages.filter(m => !m.isTemporary), botMessage];
                  });
                }
                
                // Update the unified loading status - use correct message ID
                const targetMessageId = data.messageId || botMessage?._id;
                if (targetMessageId) {
                  updateMessageStatus(targetMessageId, {
                    type: STATUS_TYPES.SUPERBUCKET_QUERY,
                    data: {}
                  });
                } else {
                  console.warn('SUPERBUCKET: No target message ID available for status update');
                }
                
                // Also set the flag on the bot message for fallback compatibility
                if (botMessage) {
                  // Update botMessage ID to match backend if different
                  const oldId = botMessage._id;
                  if (data.messageId && botMessage._id !== data.messageId) {
                    console.log('SUPERBUCKET: Updating botMessage ID to match backend:', data.messageId);
                    previousMessageId = botMessage._id;
                    botMessage._id = data.messageId;
                  }
                  
                  botMessage = {
                    ...botMessage,
                    isQueryingSuperbucket: true
                  };
                  
                  updateBotMessage(botMessage, previousMessageId || oldId);
                }
                continue;
              }
              
              // Handle superbucket progress updates
              if (currentEvent === 'tool_call_start') {
                console.log('TOOL CALL: Tool execution started', data);
                
                const targetId = data.messageId || botMessage?._id;
                if (!targetId) { continue; }
                
                const position = data.position ?? data.textPosition ?? Math.max(botMessage?.text?.length ?? 0, botMessage?._textBuffer?.length ?? 0);
                const newPosition = { position, toolId: data.toolId, functionName: data.toolName || data.functionName, type: 'mcp_tool', isComplete: false, status: data.status || 'Executing...' };
                const toolName = data.toolName || data.functionName || '';
                const isFileGen = toolName.startsWith('generate_') || toolName.startsWith('create_');
                const activeFileTool = isFileGen ? toolName : undefined;
                
                if (toolCallManager) {
                  toolCallManager.handleToolCallStart(targetId, { toolName: data.toolName || data.functionName, toolId: data.toolId, status: data.status || 'Executing...', messageId: targetId, position });
                }
                
                setMessages(prev => {
                  const list = Array.isArray(prev) ? prev : [];
                  let existing = list.find(m => m._id === targetId);
                  if (!existing && data.messageId && botMessage) {
                    const tempMatch = list.find(m => m._id === botMessage._id);
                    if (tempMatch) {
                      previousMessageId = botMessage._id;
                      botMessage._id = data.messageId;
                      const newPositions = [...(tempMatch.toolCallPositions || []), newPosition];
                      return list.map(m => m._id === previousMessageId
                        ? { ...m, _id: data.messageId, toolCallPositions: newPositions, toolCallsActive: true, hasToolCalls: true, ...(activeFileTool ? { _activeFileTool: activeFileTool } : {}) }
                        : m);
                    }
                  }
                  if (existing) {
                    const newPositions = [...(existing.toolCallPositions || []), newPosition];
                    return list.map(m => m._id === targetId
                      ? { ...m, toolCallPositions: newPositions, toolCallsActive: true, hasToolCalls: true, ...(activeFileTool ? { _activeFileTool: activeFileTool } : {}) }
                      : m);
                  }
                  if (!botMessage && targetId) {
                    const placeholder = { _id: targetId, text: '', sender: 'bot', timestamp: new Date(), isTemporary: false, isStreaming: true, toolCallPositions: [newPosition], toolCallsActive: true, hasToolCalls: true, ...(activeFileTool ? { _activeFileTool: activeFileTool } : {}) };
                    return [...list, placeholder];
                  }
                  return prev;
                });
                if (botMessage && (botMessage._id === targetId || !botMessage.toolCallPositions)) {
                  botMessage = { ...botMessage, _id: targetId, toolCallPositions: [...(botMessage.toolCallPositions || []), newPosition], toolCallsActive: true, hasToolCalls: true, ...(activeFileTool ? { _activeFileTool: activeFileTool } : {}) };
                } else if (!botMessage && targetId) {
                  botMessage = { _id: targetId, text: '', sender: 'bot', timestamp: new Date(), isTemporary: false, isStreaming: true, toolCallPositions: [newPosition], toolCallsActive: true, hasToolCalls: true, ...(activeFileTool ? { _activeFileTool: activeFileTool } : {}) };
                }
                continue;
              }
              
              if (currentEvent === 'tool_call_result') {
                console.log('TOOL CALL: Tool execution completed', data);
                
                const messageId = data.messageId || botMessage?._id;
                
                if (toolCallManager && messageId) {
                  toolCallManager.handleToolCallResult(messageId, {
                    toolName: data.toolName || data.functionName,
                    toolId: data.toolId,
                    success: data.success,
                    result: data.success ? data.result : null,
                    error: data.success ? null : data.error,
                    status: data.status,
                    recordCount: data.recordCount
                  });
                }
                
                if (messageId) {
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    const existing = currentMessages.find(m => m._id === messageId);
                    if (!existing) return prev;
                    const positions = existing.toolCallPositions || [];
                    const matchIdx = data.toolId
                      ? positions.findIndex((p) => p.toolId === data.toolId)
                      : positions.findIndex((p) => (p.functionName === (data.toolName || data.functionName)) && !p.isComplete);
                    const updatedPositions = matchIdx >= 0
                      ? positions.map((p, i) => i === matchIdx ? { ...p, isComplete: true, status: data.status || (data.success ? 'Done' : 'Error') } : p)
                      : positions;
                    const allComplete = updatedPositions.every((p) => p.isComplete);
                    const updated = { ...existing, toolCallPositions: updatedPositions.length ? updatedPositions : existing.toolCallPositions, toolCallsActive: !allComplete };
                    if (botMessage && botMessage._id === messageId) botMessage = updated;
                    return currentMessages.map(m => m._id === messageId ? updated : m);
                  });
                }
                
                continue;
              }

              if (currentEvent === 'superbucket_progress') {
                // FALLBACK: If this is the first progress event and we're querying, treat it as start
                if (data.isQueryingSuperbucket === true && data.messageId) {
                  console.log('SUPERBUCKET: First progress event detected, treating as query start');
                  
                  // Create botMessage if it doesn't exist (fallback for missed start event)
                  if (!botMessage) {
                    console.log('🤖 BOT: Creating bot message for superbucket query (progress fallback) with ID:', data.messageId);
                    botMessage = {
                      _id: data.messageId,
                      text: '[[LOADING_INDICATOR]]',
                      sender: 'bot',
                      timestamp: new Date(),
                      isTemporary: false,
                      isStreaming: true,
                      isThinking: false,
                      isQueryingSuperbucket: true
                    };
                    
                    setMessages(prev => {
                      const currentMessages = Array.isArray(prev) ? prev : [];
                      return [...currentMessages.filter(m => !m.isTemporary), botMessage];
                    });
                  }
                  
                  // Always set the superbucket query status to ensure it's active
                  console.log('SUPERBUCKET: Setting SUPERBUCKET_QUERY status from progress event');
                  updateMessageStatus(data.messageId, {
                    type: STATUS_TYPES.SUPERBUCKET_QUERY,
                    data: {}
                  });
                }
                
                if (data.isQueryingSuperbucket === false) {
                  console.log('SUPERBUCKET: Query progress completed, clearing loading state');
                  
                  // Clear the superbucket query status
                  updateMessageStatus(data.messageId, {
                    type: STATUS_TYPES.COMPLETE,
                    data: {}
                  });
                  
                  // Clear the flag on the bot message
                  if (botMessage) {
                    botMessage = {
                      ...botMessage,
                      isQueryingSuperbucket: false
                    };
                    
                    setMessages(prev => {
                      const currentMessages = Array.isArray(prev) ? prev : [];
                      return [...currentMessages.filter(m => m._id !== botMessage._id), botMessage];
                    });
                  }
                }
                continue;
              }

              // Handle deep research progress updates
              if (data.type === 'deep_research_progress') {
                console.log('🧠 Frontend received deep research progress:', {
                  messageId: data.messageId,
                  stage: data.stage,
                  progress: data.progress,
                  message: data.message
                });
                
                // Also update unified system for deep research
                updateMessageStatus(data.messageId, {
                  type: STATUS_TYPES.DEEP_RESEARCH,
                  data: {
                    stage: data.stage,
                    progress: data.progress,
                    message: data.message
                  }
                });
                
                setDeepResearchProgress({
                  stage: data.stage,
                  progress: data.progress,
                  message: data.message
                });
                continue;
              }
              
              if (data.type === 'message_created' && data.message) {
                console.log('MESSAGE_CREATED event received:', {
                  messageId: data.message._id,
                  currentBotMessageId: botMessage?._id,
                  willReplace: !botMessage
                });
                
                if (!botMessage) {
                  const hasTextContent = Boolean(data.message.text && data.message.text.trim() !== '');
                  botMessage = {
                    ...data.message,
                    isTemporary: false,
                    text: data.message.text || '',
                    // Ensure streaming flag is true until first content arrives
                    isStreaming: data.message.isStreaming ?? !hasTextContent
                  };
                  
                  console.log('MESSAGE: Bot message created with ID:', botMessage._id);
                  
                  // Set default thinking state for new bot messages
                  if (botMessage._id && !botMessage.isDeepResearch) {
                    updateMessageStatus(botMessage._id, {
                      type: STATUS_TYPES.THINKING,
                      data: { dots: true }
                    });
                  }
                  
                  // If this is a deep research message, apply any existing progress state
                  if (deepResearchProgress && data.message.isDeepResearch) {
                    botMessage = {
                      ...botMessage,
                      isDeepResearch: true,
                      metadata: {
                        ...botMessage.metadata,
                        stage: deepResearchProgress.stage,
                        progress: deepResearchProgress.progress
                      }
                      // Remove duplicate text - progress bar handles visual feedback
                    };
                  }
                  
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    return [...currentMessages.filter(m => !m.isTemporary), botMessage];
                  });
                } else if (botMessage && data.message._id && botMessage._id !== data.message._id) {
                  // Update temp ID to real ID when message_created arrives late
                  console.log('MESSAGE_CREATED: Updating temp ID to real ID:', {
                    from: botMessage._id,
                    to: data.message._id
                  });
                  
                  const oldId = botMessage._id;
                  previousMessageId = botMessage._id;
                  
                  // Merge the real message data with our streaming content
                  botMessage = {
                    ...botMessage,
                    ...data.message,
                    _id: data.message._id,
                    // Preserve streaming content that we've already accumulated
                    text: botMessage.text || data.message.text || '',
                    _textBuffer: botMessage._textBuffer,
                    isStreaming: botMessage.isStreaming ?? data.message.isStreaming,
                    isTemporary: false
                  };
                  
                  console.log('MESSAGE_CREATED: Updated botMessage:', {
                    id: botMessage._id,
                    isTemporary: botMessage.isTemporary,
                    textLength: botMessage.text?.length
                  });
                  
                  // Update messages array, removing old temp ID
                  updateBotMessage(botMessage, oldId);
                }
              } else if (data.type === 'message_update' && botMessage) {
                // Append content chunks instead of replacing entire message
                if (data.message.text) {
                  // Check if this is a content chunk that should be appended
                  const shouldAppend = botMessage.text && 
                                      botMessage.text !== '[[LOADING_INDICATOR]]' && 
                                      botMessage.text !== 'Generating response...' &&
                                      botMessage.text.trim() !== '';
                  
                  if (shouldAppend) {
                    // Append the new content to existing text
                    botMessage = {
                      ...botMessage,
                      text: botMessage.text + data.message.text,
                      isThinking: false,
                      isStreaming: true,
                      isTemporary: false
                    };
                  } else {
                    // Replace placeholder text with first real content
                    botMessage = {
                      ...botMessage,
                      text: data.message.text,
                      isThinking: false,
                      isStreaming: true,
                      isTemporary: false
                    };
                  }
                } else {
                  // Update other properties without touching text
                  botMessage = { 
                    ...botMessage, 
                    ...data.message,
                    // Preserve existing text if no new text provided
                    text: botMessage.text
                  };
                }
                
                // If this is a deep research message update, preserve progress data
                if (deepResearchProgress && botMessage.isDeepResearch) {
                  botMessage = {
                    ...botMessage,
                    metadata: {
                      ...botMessage.metadata,
                      stage: deepResearchProgress.stage,
                      progress: deepResearchProgress.progress
                    }
                  };
                }
                
                setMessages(prev => {
                  const currentMessages = Array.isArray(prev) ? prev : [];
                  return [...currentMessages.filter(m => m._id !== botMessage._id), botMessage];
                });
              } else if (data.type === 'memory_update') {
                // Handle memory update event - update message with memory metadata
                console.log('🧠 Frontend received memory_update:', data);
                
                // If botMessage doesn't exist yet, find the most recent bot placeholder
                if (!botMessage) {
                  // Try to find the bot message from current messages state
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    const lastBotMessage = [...currentMessages].reverse().find(msg => 
                      msg.sender === 'bot' && (msg.isTemporary || msg.text === '[[LOADING_INDICATOR]]')
                    );
                    
                    if (lastBotMessage) {
                      console.log('🧠 Found bot placeholder message for memory update:', lastBotMessage._id);
                      botMessage = { ...lastBotMessage };
                    } else {
                      // Race condition: WebSocket message might not have arrived yet
                      // Set a timeout to try again after a short delay
                      console.log('🧠 Bot message not found yet, will retry in 100ms');
                      setTimeout(() => {
                        setMessages(prevRetry => {
                          const currentMessagesRetry = Array.isArray(prevRetry) ? prevRetry : [];
                          const lastBotMessageRetry = [...currentMessagesRetry].reverse().find(msg => 
                            msg.sender === 'bot' && (msg.isTemporary || msg.text === '[[LOADING_INDICATOR]]')
                          );
                          
                          if (lastBotMessageRetry) {
                            console.log('🧠 Found bot placeholder message on retry:', lastBotMessageRetry._id);
                            // Apply memory metadata to the found message
                            return currentMessagesRetry.map(msg => 
                              msg._id === lastBotMessageRetry._id ? {
                                ...msg,
                                hasMemoryUpdate: true,
                                memoryAction: data.memoryAction,
                                memoryContent: data.memoryContent
                              } : msg
                            );
                          } else {
                            console.warn('🧠 Still no bot message found for memory update after retry');
                          }
                          return prevRetry;
                        });
                      }, 100);
                    }
                    
                    return currentMessages;
                  });
                }
                
                if (botMessage?._id) {
                  console.log('🧠 Updating message with memory metadata:', {
                    messageId: botMessage._id,
                    memoryAction: data.memoryAction,
                    memoryContent: data.memoryContent
                  });
                  
                  // Update botMessage reference
                  botMessage = {
                    ...botMessage,
                    hasMemoryUpdate: true,
                    memoryAction: data.memoryAction,
                    memoryContent: data.memoryContent
                  };
                  
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    const updatedMessages = currentMessages.map(msg => 
                      msg._id === botMessage._id ? {
                        ...msg,
                        hasMemoryUpdate: true,
                        memoryAction: data.memoryAction,
                        memoryContent: data.memoryContent
                      } : msg
                    );
                    console.log('🧠 Messages updated with memory metadata');
                    return updatedMessages;
                  });
                } else {
                  console.warn('🧠 No bot message found for memory update');
                }
              } else if (data.type === 'stream_complete') {
                // Handle memory metadata from stream_complete event
                if (data.hasMemoryUpdate && botMessage?._id) {
                  setMessages(prev => {
                    const currentMessages = Array.isArray(prev) ? prev : [];
                    return currentMessages.map(msg => 
                      msg._id === botMessage._id ? {
                        ...msg,
                        hasMemoryUpdate: true,
                        memoryAction: data.memoryAction,
                        memoryContent: data.memoryContent
                      } : msg
                    );
                  });
                }
                
                // Clear all loading states when streaming completes
                if (botMessage?._id) {
                  updateMessageStatus(botMessage._id, {
                    type: STATUS_TYPES.COMPLETE,
                    data: {}
                  });
                }
                
                // Clear deep research progress when complete
                if (deepResearchProgress) {
                  setTimeout(() => {
                    setDeepResearchProgress(null);
                  }, 2000);
                }
                
                // Only navigate to a different chat if we're at the root chat page (no chatId)
                // This prevents unwanted navigation when background chats receive updates
                if (onChatCreated && data.chatId && !chatId) {
                  console.log('New chat created during streaming, navigating:', data.chatId);
                  onChatCreated(data.chatId);
                }
                
                setIsLoading(false);
                
                // Reset SSE streaming flag after delay to allow WebSocket to ignore completion
                setTimeout(() => {
                  sseStreamingActive.current = false;
                }, 100);
                break;
              } else if (data.type === 'error') {
                console.error('Stream error:', data.error);
                setError(data.error);
                setDeepResearchProgress(null);
                // Reset SSE streaming flag on error
                sseStreamingActive.current = false;
                
                setIsLoading(false);
                break;
              }
                          } catch (parseError) {
                console.warn('Failed to parse SSE data:', parseError);
                // Continue processing other lines even if one fails
              }
          }
        }
      }

    } catch (error) {
      console.error('Send message error:', error);
      setError(error.message);
      setDeepResearchProgress(null);
      // Reset SSE streaming flag on any error
      sseStreamingActive.current = false;
      
      setIsLoading(false);
    }
  }, [setMessages, setError, setIsLoading, sseStreamingActive, currentUser, chatId, onChatCreated, projectId, deepResearchProgress, _startFileJobPoll]);

  // Cancel response function for stopping streaming
  const cancelResponse = useCallback(async () => {
    console.log('Cancel response called');
    
    try {
      // Try to cancel any active streaming
      if (chatId) {
        try {
          console.log('Cancelling active stream for chat:', chatId);
          const token = localStorage.getItem("token");
          
          // Make a cancellation request to the streaming endpoint
          const cancelResponseApi = await authenticatedFetch(`${process.env.REACT_APP_API_URL}/chat/${chatId}/messages/stream/cancel`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          const cancelResult = await cancelResponseApi.json();
          console.log('Cancellation response:', cancelResult);
          
          // Update UI state immediately to show cancellation - override minimum loading time
          setIsLoading(false);
          setDeepResearchProgress(null);
          sseStreamingActive.current = false;
          
          // Update messages to mark the most recent bot message as cancelled
          setMessages(prev => {
            const currentMessages = Array.isArray(prev) ? prev : [];
            
            // Find the most recent temporary/streaming bot message
            const tempBotMessages = currentMessages.filter(msg => 
              msg && msg.sender === 'bot' && (msg.isTemporary || msg.isThinking || msg.isStreaming)
            );
            
            if (tempBotMessages.length > 0) {
              const mostRecentBotMessage = tempBotMessages[tempBotMessages.length - 1];
              
              return currentMessages.map(msg => {
                if (msg._id === mostRecentBotMessage._id) {
                  return {
                    ...msg,
                    isTemporary: false,
                    isThinking: false,
                    isStreaming: false,
                    isCancelled: true
                  };
                }
                return msg;
              });
            }
            
            return currentMessages;
          });
          
        } catch (cancelErr) {
          console.warn('Error during stream cancellation:', cancelErr);
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error in cancelResponse:', error);
      return false;
    }
  }, [chatId, setMessages, setIsLoading, setDeepResearchProgress, sseStreamingActive]);

  return {
    handleSend,
    cancelResponse,
    deepResearchProgress,
    applyFileJobResult,
  };
};

// EXAMPLE: Unified loading system integration helpers
// You can gradually add these calls to your existing handlers
export const useUnifiedLoadingHelpers = () => {
  const { updateMessageStatus, STATUS_TYPES } = useMessageStatus();
  
  return {
    // Image generation helpers
    setImageGenerationLoading: (messageId, optimalSize = 'auto') => {
      updateMessageStatus(messageId, {
        type: STATUS_TYPES.GENERATING_IMAGE,
        data: { optimalSize }
      });
    },

    // Search helpers - DEPRECATED: SearchIndicator is handled directly by ChatMessage
    // These are kept for backward compatibility but won't trigger indicators
    setSearchLoading: (messageId, isHubSpot = false) => {
      updateMessageStatus(messageId, {
        type: STATUS_TYPES.SEARCHING,
        data: {
          message: isHubSpot ? 'Searching HubSpot...' : 'Searching the web...',
          sources: [],
          isHubSpot
        }
      });
    },

    // Document analysis helpers
    setAnalysisLoading: (messageId, message = 'Analyzing document...') => {
      updateMessageStatus(messageId, {
        type: STATUS_TYPES.ANALYZING,
        data: { message, progress: 0 }
      });
    },

    // Clear any loading state
    clearLoading: (messageId) => {
      updateMessageStatus(messageId, {
        type: STATUS_TYPES.COMPLETE,
        data: {}
      });
    }
  };
}; 